import { useQuery } from '@tanstack/react-query';
import { DateTimeInput } from '@/shared/ui/DateTimeInput';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { MultiSelect } from '@/shared/ui/MultiSelect';
import type { FilterValue, FilterValues, ListFilter } from './types';

const OPTIONS_STALE_MS = 5 * 60_000;

type Props = {
  filter: ListFilter;
  draft: FilterValues;
  /** Owning list's query key, scopes the remote options cache. */
  listKey: string;
  onChange: (value: FilterValue) => void;
};

/** Single filter input of `GenericListPage`, picked by `filter.kind`. */
export const ListFilterControl = ({ filter, draft, listKey, onChange }: Props) => {
  const parent = filter.dependsOn ? String(draft[filter.dependsOn] ?? '') : '';
  const remote = useQuery({
    queryKey: ['list-filter-options', listKey, filter.name, parent],
    queryFn: () => (filter.loadOptions ? filter.loadOptions(parent) : []),
    enabled: Boolean(filter.loadOptions),
    staleTime: OPTIONS_STALE_MS,
  });
  const options = filter.loadOptions ? (remote.data ?? []) : (filter.options ?? []);
  const value = draft[filter.name];

  switch (filter.kind) {
    case 'select':
      return (
        <FilterSelect
          options={options}
          value={String(value)}
          isLoading={remote.isLoading}
          placeholder={filter.placeholder}
          onChange={onChange}
        />
      );
    case 'multiselect':
      return (
        <MultiSelect
          options={options}
          value={Array.isArray(value) ? value : []}
          isLoading={remote.isLoading}
          placeholder={filter.placeholder}
          onChange={onChange}
        />
      );
    case 'datetime':
      return (
        <DateTimeInput value={String(value)} type={filter.inputType} onChange={onChange} />
      );
    case 'text':
      return (
        <input
          className="form-control input-sm"
          style={{ width: 250 }}
          placeholder={filter.placeholder ?? 'Значение...'}
          value={String(value)}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
};
